import { Currency, Incoterms, TradeSignalSourceType, useApiAccountsV1UserRetrieve } from 'api';
import { formatDateToIsoString } from 'date';
import { convertToMaterialComponentInput } from 'features';
import { Form, generatePriceFieldValues, useForm, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { useNavigate } from 'react-router-dom';
import { ActionRow, Button, Page, Section } from 'ui';

import { DemandSignalFormSection } from '../../components/DemandSignalFormSection/DemandSignalFormSection';
import { useCreateDemandSignal } from '../../hooks/useCreateDemandSignal/useCreateDemandSignal';
import {
  type DemandSignalFormInputValues,
  type DemandSignalFormValues,
  DemandSignalSchema,
} from '../../schema/DemandSignalSchema/DemandSignalSchema';

const DEFAULT_VALUES: Partial<DemandSignalFormValues> = {
  currency: Currency.GBP,
  incoterm: Incoterms.CIF,
  material_component_values: [],
  notes: '',
  price_as_index_pct_enabled: false,
  source_type: TradeSignalSourceType.GROUND_CONTROL,
};

export const DemandSignalCreate = () => {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const form = useForm<DemandSignalFormInputValues, unknown, DemandSignalFormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(DemandSignalSchema),
  });
  const { data: user } = useApiAccountsV1UserRetrieve();
  const { mutate: createDemandSignal, isPending } = useCreateDemandSignal(form);

  const onSubmit = (values: DemandSignalFormValues) => {
    const {
      trade_company_lead_id,
      trade_company_id,
      latest_delivery_date,
      port_of_discharge_id, // eslint-disable-next-line unused-imports/no-unused-vars
      price_as_index_percentage,
      tonnes,
      material_component_values,
      buyer_material_spec_id,
      ...restValues
    } = values;
    const materialComponents = convertToMaterialComponentInput(material_component_values || []);

    createDemandSignal({
      data: {
        ...restValues,
        ...generatePriceFieldValues(values),
        buyer: {
          trade_company_id: trade_company_id,
        },
        buyer_lead_id: trade_company_lead_id,
        buyer_material_spec_id: buyer_material_spec_id ?? undefined,
        created_by_id: user?.id,
        latest_delivery_date: latest_delivery_date ? formatDateToIsoString(latest_delivery_date) : undefined,
        material_component_values: materialComponents,
        port_of_discharge: {
          id: port_of_discharge_id,
        },
        tonnes: tonnes.toString(),
      },
    });
  };

  return (
    <Page>
      <Page.Title title={t('tradeSignals.demandPage.title')} />
      <Page.Divider />
      <Section>
        <Form<DemandSignalFormInputValues, unknown, DemandSignalFormValues> {...form} onSubmit={onSubmit}>
          <DemandSignalFormSection />
          <ActionRow>
            <Button size="large" fullWidth onClick={goBack} variant="secondary">
              {t('tradeSignals.common.actions.cancel')}
            </Button>
            <Button size="large" fullWidth loading={isPending} type="submit">
              {t('tradeSignals.common.actions.save')}
            </Button>
          </ActionRow>
        </Form>
      </Section>
    </Page>
  );
};
